import { getDataSource, removeDataSource, updateDataSource } from '@/api';
import { Block, Divider, Grid, Spinner } from '@prometheus/components';
import {
  NestedInfoWrapper,
  DatabaseBaseInfo,
  SecondaryContentWrapper,
  TabsNavigationNew,
  SpinnerContainer,
  ConfirmationDialog,
} from '@prometheus/features';
import {
  DialogContext,
  UseDialogContext,
  useEntityNew,
} from '@prometheus/hooks';
import { Route, Routes, useParams } from 'react-router-dom';
import { tabs } from './DatabaseEntity.config';
import { FC, useContext, useEffect } from 'react';
import { Status } from '@prometheus/types';
import { ExecutionsList } from './ExecutionsList/ExecutionsList';
import { OmopCdmSettings } from './OmopCdmSettings';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setBreadcrumbs } from '@prometheus/redux';
import { ConnectionDetails } from './ConnectionDetails';

export const DatabaseEntity: FC<{
  onPublish: (id: string) => void;
  root: { rootPath: string; rootTitle: string };
}> = ({ onPublish, root }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { showDialog, hideDialog } =
    useContext<UseDialogContext>(DialogContext);

  const { entity, status, updateEntity, deleteEntity, ...rest } = useEntityNew(
    {
      get: getDataSource,
      update: updateDataSource,
      delete: removeDataSource,
    },
    id
  );

  const handleSave = (newValue: any, keyFile?: any, isAdmin?: boolean) => {
    let fd = new FormData();
    fd.append(
      'datasource',
      new Blob([JSON.stringify(newValue)], {
        type: 'application/json',
      })
    );

    fd.append(isAdmin ? 'adminKeyfile' : 'keyfile', keyFile);
    updateEntity(fd);
  };

  useEffect(() => {
    root &&
      entity &&
      dispatch(
        setBreadcrumbs([
          {
            name: t('breadcrumbs.admin'),
            path: `${root.rootPath}`,
          },
          {
            name: t('breadcrumbs.databases'),
            path: `${root.rootPath}/databases`,
          },
          {
            name: entity.name,
            path: `${root.rootPath}/databases/${entity.id}`,
          },
        ])
      );
  }, [root, entity]);

  const handleDelete = () => {
    showDialog<any>(ConfirmationDialog, {
      onSubmit: deleteEntity,
      onClose: hideDialog,
      text: t('modules.administration.databases.delete_message_database'),
      confirmButtonText: 'DELETE',
      variant: 'error',
    });
  };

  const handlePublish = () => {
    onPublish(id);
  };

  if (status === Status.INITIAL || status === Status.IN_PROGRESS) {
    return (
      <SpinnerContainer>
        <Spinner size={62} />
      </SpinnerContainer>
    );
  }
  return (
    <Grid container px={6} flexGrow={1} flexDirection="column">
      <NestedInfoWrapper>
        <DatabaseBaseInfo
          entity={entity}
          onSubmit={handleSave}
          onDelete={handleDelete}
          onPublish={handlePublish}
        />
      </NestedInfoWrapper>
      <Grid container>
        <Grid item xs={12}>
          <TabsNavigationNew tabs={tabs()} withRouting secondary />
          <Divider />
        </Grid>
        <Grid item xs={12} py={3}>
          <SecondaryContentWrapper>
            <Routes>
              <Route
                index
                element={
                  <ConnectionDetails
                    updateEntity={handleSave}
                    entity={entity}
                  />
                }
              />
              <Route
                path="omop-cdm-config/*"
                element={
                  <Block>
                    <OmopCdmSettings
                      entity={entity}
                      updateEntity={handleSave}
                    />
                    <Divider sx={{ mx: 2 }} />
                    <ExecutionsList dataSourceId={id} dataSource={entity} />
                  </Block>
                }
              />
            </Routes>
          </SecondaryContentWrapper>
        </Grid>
      </Grid>
    </Grid>
  );
};
